import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Search, Loader2, Camera, Pencil, TrendingDown, TrendingUp, MessageCircle } from "lucide-react";
import DesktopLayout from "@/components/Layout/DesktopLayout";
import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/contexts/AuthContext";

const inr = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
const inrK = (v) => {
  const n = Number(v || 0);
  if (Math.abs(n) >= 1e7) return `₹${(n / 1e7).toFixed(2)} Cr`;
  if (Math.abs(n) >= 1e5) return `₹${(n / 1e5).toFixed(2)} L`;
  if (Math.abs(n) >= 1e3) return `₹${(n / 1e3).toFixed(1)} K`;
  return `₹${n.toFixed(0)}`;
};

const stageLabel = (s) => (s || "—").replaceAll("_", " ");

// Build a wa.me link with a pre-filled reminder message.
// Strips non-digits from the phone and prepends India country code (91) if
// the number is a bare 10-digit local number.
const normalisePhone = (phone) => {
  const digits = String(phone || "").replace(/\D/g, "");
  if (!digits) return "";
  if (digits.length === 10) return `91${digits}`;
  if (digits.length === 11 && digits.startsWith("0")) return `91${digits.slice(1)}`;
  if (digits.length === 12 && digits.startsWith("91")) return digits;
  return digits; // already includes country code or non-Indian
};

const buildWhatsAppLink = (r) => {
  const phone = normalisePhone(r.customer_phone);
  if (!phone) return "";
  const firstName = String(r.customer_name || "").trim().split(/\s+/)[0] || "there";
  const projectRef = r.project_id || r.name || "your project";
  const orderVal = inr(r.order_value);
  const received = inr(r.amount_received);
  const balance = inr(r.balance);
  const lines = [
    `Hello ${firstName},`,
    ``,
    `This is a friendly reminder from Hygiene Kitchens regarding your project ${projectRef}.`,
    ``,
    `• Order value: ${orderVal}`,
    `• Amount received: ${received}`,
    `• Pending balance: ${balance}`,
    ``,
    `Kindly arrange the pending payment at your earliest convenience so we can proceed smoothly.`,
    ``,
    `Thank you!`,
    `— Hygiene Kitchens`,
  ];
  return `https://wa.me/${phone}?text=${encodeURIComponent(lines.join("\n"))}`;
};

export default function SalesReceivables() {
  const { user } = useAuth();
  const canEditReceived = ["super_admin", "accounts"].includes(user?.role);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [stageFilter, setStageFilter] = useState("all");
  const [balanceFilter, setBalanceFilter] = useState("all");   // all | pending | cleared
  const [editing, setEditing] = useState(null);                // {id, value}

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/project/sales-receivables"); setData(data); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const rows = data?.rows || [];
  const stages = useMemo(() => {
    const s = new Set(rows.map((r) => r.stage).filter(Boolean));
    return ["all", ...Array.from(s)];
  }, [rows]);

  const filtered = useMemo(() => {
    let out = rows;
    const needle = q.trim().toLowerCase();
    if (needle) {
      out = out.filter((r) =>
        [r.project_id, r.name, r.customer_name, r.customer_address,
          r.dealer_name, r.sales_person_name, r.customer_phone]
          .filter(Boolean)
          .some((s) => String(s).toLowerCase().includes(needle))
      );
    }
    if (stageFilter !== "all") out = out.filter((r) => r.stage === stageFilter);
    if (balanceFilter === "pending") out = out.filter((r) => r.balance > 0);
    if (balanceFilter === "cleared") out = out.filter((r) => r.balance <= 0);
    return out;
  }, [rows, q, stageFilter, balanceFilter]);

  const totals = useMemo(() => {
    const order = filtered.reduce((s, r) => s + Number(r.order_value || 0), 0);
    const recv = filtered.reduce((s, r) => s + Number(r.amount_received || 0), 0);
    return { order, recv, bal: order - recv };
  }, [filtered]);

  const saveReceived = async (r) => {
    const val = Number(editing?.value);
    if (isNaN(val) || val < 0) { toast.error("Enter a valid amount"); return; }
    try {
      await api.patch(`/project/${r.id}/received-amount`, { received_total: val });
      toast.success("Amount received updated");
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <DesktopLayout>
      <div className="max-w-[1700px] mx-auto p-6 space-y-4" data-testid="sales-receivables-page">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="hk-tiny-label">Sales</div>
            <h1 className="text-3xl font-black tracking-tight text-slate-900">Pending Receivables</h1>
            <div className="text-xs text-slate-500 mt-1">
              Every converted project — order value vs amount received vs balance to collect.
              Sales users see only their own projects.
            </div>
          </div>
          <button type="button" onClick={load} disabled={loading}
            className="px-3 py-2 text-xs font-bold uppercase border border-slate-300 rounded-sm hover:bg-slate-50 disabled:opacity-50"
            data-testid="sales-refresh">
            {loading ? <Loader2 className="w-3 h-3 animate-spin inline mr-1" /> : null} Refresh
          </button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="sales-totals">
          <Tile label="Projects" value={filtered.length} sub={`of ${rows.length}`} />
          <Tile label="Order value" value={inrK(totals.order)} sub={inr(totals.order)} tone="#0f172a" />
          <Tile label="Received" value={inrK(totals.recv)} sub={inr(totals.recv)} tone="#15803d" icon={TrendingUp} />
          <Tile label="Balance" value={inrK(totals.bal)} sub={inr(totals.bal)}
            tone={totals.bal > 0 ? "#c2410c" : "#15803d"} icon={TrendingDown} />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="w-4 h-4 absolute left-2 top-2.5 text-slate-400" />
            <input className="w-full border border-slate-300 rounded-sm pl-8 pr-3 py-2 text-sm"
              placeholder="Search project id · name · customer · address · sales / dealer · phone"
              value={q} onChange={(e) => setQ(e.target.value)}
              data-testid="sales-search" />
          </div>
          <select className="border border-slate-300 rounded-sm px-3 py-2 text-sm"
            value={stageFilter} onChange={(e) => setStageFilter(e.target.value)}
            data-testid="sales-stage-filter">
            {stages.map((s) => (
              <option key={s} value={s}>{s === "all" ? "All stages" : stageLabel(s)}</option>
            ))}
          </select>
          <div className="inline-flex rounded-sm overflow-hidden border border-slate-300">
            {[["all", "All"], ["pending", "Pending"], ["cleared", "Cleared"]].map(([k, l]) => (
              <button key={k} type="button" onClick={() => setBalanceFilter(k)}
                data-testid={`sales-bal-${k}`}
                className={`px-3 py-2 text-xs font-bold uppercase ${balanceFilter === k ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
                {l}
              </button>
            ))}
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
          <table className="w-full text-xs" data-testid="sales-table">
            <thead className="bg-slate-50 border-b-2 border-slate-200">
              <tr className="text-left">
                <Th>Customer</Th>
                <Th>Project</Th>
                <Th>Address</Th>
                <Th>Sales / Dealer</Th>
                <Th>Converted</Th>
                <Th>Dispatched</Th>
                <Th>Install done</Th>
                <Th align="right">Order value</Th>
                <Th align="right">Received</Th>
                <Th align="right">Balance</Th>
                <Th>Status</Th>
                <Th align="center">Photos</Th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={12} className="text-center py-8 text-slate-400">Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={12} className="text-center py-8 text-slate-400" data-testid="sales-empty">
                  No projects match the current filters.
                </td></tr>
              ) : (
                filtered.map((r) => {
                  const balTone = r.balance > 0 ? "text-orange-700" : "text-emerald-700";
                  return (
                    <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`sales-row-${r.id}`}>
                      <td className="px-3 py-2">
                        <div className="font-bold text-slate-900 truncate max-w-[180px]">{r.customer_name || "—"}</div>
                        {r.customer_phone && (
                          <div className="text-[10px] text-slate-500">{r.customer_phone}</div>
                        )}
                        {r.balance > 0 && r.customer_phone && (
                          <a href={buildWhatsAppLink(r)} target="_blank" rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 mt-1 text-[10px] font-black uppercase text-emerald-800 bg-emerald-100 hover:bg-emerald-200 border border-emerald-300 px-1.5 py-0.5 rounded-sm"
                            title="Send WhatsApp reminder with pending balance"
                            data-testid={`sales-whatsapp-${r.id}`}>
                            <MessageCircle className="w-3 h-3" /> Nudge
                          </a>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <Link to={`/projects/${r.id}`}
                          className="text-orange-700 hover:underline font-medium block truncate max-w-[200px]">
                          {r.name || "—"}
                        </Link>
                        <div className="font-mono text-[10px] text-slate-500 max-w-[200px] truncate">
                          {r.project_id || ""}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-slate-600 max-w-[220px]">
                        <div className="line-clamp-2">{r.customer_address || <span className="text-slate-400">—</span>}</div>
                      </td>
                      <td className="px-3 py-2 text-slate-700 max-w-[140px] truncate">
                        {r.dealer_name || r.sales_person_name || <span className="text-slate-400">—</span>}
                      </td>
                      <td className="px-3 py-2 text-slate-600 text-[11px] whitespace-nowrap">
                        {r.converted_at ? new Date(r.converted_at).toLocaleDateString() : "—"}
                      </td>
                      <td className="px-3 py-2 text-slate-600 text-[11px] whitespace-nowrap">
                        {r.dispatched_at ? new Date(r.dispatched_at).toLocaleDateString() : <span className="text-slate-400">—</span>}
                      </td>
                      <td className="px-3 py-2 text-slate-600 text-[11px] whitespace-nowrap">
                        {r.installation_completed_at
                          ? new Date(r.installation_completed_at).toLocaleDateString()
                          : <span className="text-slate-400">—</span>}
                      </td>
                      <td className="px-3 py-2 text-right font-mono font-bold text-slate-900">
                        {inr(r.order_value)}
                      </td>
                      <td className="px-3 py-2 text-right font-mono">
                        {editing?.id === r.id ? (
                          <div className="inline-flex items-center gap-1">
                            <input type="number" step="0.01" min="0"
                              className="border border-slate-300 rounded-sm px-1 py-0.5 w-24 text-right text-xs"
                              value={editing.value}
                              onChange={(e) => setEditing({ ...editing, value: e.target.value })}
                              data-testid={`sales-received-input-${r.id}`}
                              autoFocus />
                            <button type="button" onClick={() => saveReceived(r)}
                              className="text-emerald-700 font-bold text-[10px] uppercase"
                              data-testid={`sales-received-save-${r.id}`}>Save</button>
                            <button type="button" onClick={() => setEditing(null)}
                              className="text-slate-500 text-[10px]">Cancel</button>
                          </div>
                        ) : (
                          <span className="inline-flex items-center gap-1">
                            {inr(r.amount_received)}
                            {canEditReceived && (
                              <button type="button"
                                onClick={() => setEditing({ id: r.id, value: r.amount_received })}
                                className="text-slate-400 hover:text-orange-700"
                                title="Edit received amount"
                                data-testid={`sales-received-edit-${r.id}`}>
                                <Pencil className="w-3 h-3" />
                              </button>
                            )}
                          </span>
                        )}
                      </td>
                      <td className={`px-3 py-2 text-right font-mono font-black ${balTone}`}
                        data-testid={`sales-balance-${r.id}`}>
                        {inr(r.balance)}
                      </td>
                      <td className="px-3 py-2">
                        <span className="text-[10px] uppercase font-bold text-slate-600">{stageLabel(r.stage)}</span>
                        {r.sub_status && (
                          <div className="text-[9px] text-slate-400">{stageLabel(r.sub_status)}</div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <Link to={`/projects/${r.id}#installation`}
                          className="inline-flex items-center gap-1 text-orange-700 hover:text-orange-900"
                          title={`${r.install_photo_count} installation photo${r.install_photo_count === 1 ? "" : "s"}`}
                          data-testid={`sales-photos-${r.id}`}>
                          <Camera className="w-3.5 h-3.5" />
                          <span className="text-[10px] font-bold">{r.install_photo_count || 0}</span>
                        </Link>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="text-[10px] text-slate-400">
          Received amount can be edited inline by Super Admin / Accounts.
          Sales users see only projects assigned to them or created by them.
        </div>
      </div>
    </DesktopLayout>
  );
}

function Th({ children, align = "left" }) {
  return (
    <th className={`px-3 py-2 font-bold uppercase text-[10px] tracking-wide text-slate-500 ${align === "right" ? "text-right" : align === "center" ? "text-center" : "text-left"}`}>
      {children}
    </th>
  );
}

function Tile({ icon: Icon, label, value, sub, tone }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-4">
      <div className="flex items-center gap-2">
        {Icon && <Icon className="w-3.5 h-3.5" style={tone ? { color: tone } : {}} />}
        <div className="hk-tiny-label">{label}</div>
      </div>
      <div className="text-2xl font-black tracking-tight mt-1"
        style={tone ? { color: tone } : {}}>
        {value}
      </div>
      {sub && <div className="text-[10px] font-mono text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}
